import React from "react";

/**
 * React hook to generate an ID based on the provided seed props. This is mainly
 * useful for quickly generating strings from unknown values at runtime.
 */
export function useUIDSeed(...seedProps: unknown[]) {
  const uidPrefix = React.useMemo(
    () =>
      seedProps
        .filter(nonPrimitives)
        .map(toString)
        .map((prop) => dashify(prop))
        .join("-"),
    seedProps
  );
  return React.useCallback(
    (someString: string) => {
      return uidPrefix + "-" + dashify(`${someString}`);
    },
    [uidPrefix]
  );
}

/**
 * Rather than add a dependency to our codebase for like 7 lines of code that
 * hasn't updated in 6 years, we're just duplicating it here. Nobody else needs
 * this, and it's not worth the overhead of adding a dependency.
 *
 * https://github.com/jonschlinkert/dashify/blob/master/index.js
 */
function dashify(str: string): string {
  return str
    .trim()
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/\W/g, (m) => (/[À-ž]/.test(m) ? m : "-"))
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
}

function nonPrimitives(anything: unknown) {
  const type = typeof anything;
  if (type === "string") {
    return true;
  }
  if (type === "number") {
    return true;
  }
  if (type === "boolean") {
    return true;
  }
  return false;
}

function toString(primitive: unknown) {
  return `${primitive}`;
}
